import React, { useState } from 'react';
import { ExternalLink, ChevronDown } from 'lucide-react';
import SEO from '@/components/SEO';
import { Pill, Button } from '@/components/flat';
import { timeAgo, NewsItem } from '@/hooks/useRadarData';
import { useRadarNews } from '@/hooks/useRadarQueries';

const NEWS_PAGE_SIZE = 10;

// ── Severity classification ────────────────────────────────────────────────────

function severity(title: string): 'high' | 'medium' | 'low' {
  const t = title.toLowerCase();
  const high = ['breach', 'ransomware', 'zero-day', 'exploit', 'critical', 'emergency', 'hijack', 'takeover'];
  const med  = ['vulnerability', 'attack', 'phishing', 'malware', 'hack', 'outage', 'down', 'ddos'];
  if (high.some(k => t.includes(k))) return 'high';
  if (med.some(k => t.includes(k))) return 'medium';
  return 'low';
}

const SEV_DOT  = { high: 'bg-red-500',  medium: 'bg-amber-400', low: 'bg-blue-400' };
const SEV_LINE = { high: 'bg-red-100',  medium: 'bg-amber-100', low: 'bg-blue-100' };

// ── Incident row ──────────────────────────────────────────────────────────────

function IncidentRow({ item, isLast }: { item: NewsItem; isLast: boolean }) {
  const sev = severity(item.title);
  return (
    <div className="flex gap-4">
      <div className="flex flex-col items-center">
        <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 mt-1 ${SEV_DOT[sev]}`} />
        {!isLast && <span className={`w-0.5 flex-1 mt-1 ${SEV_LINE[sev]}`} />}
      </div>
      <div className={`flex-1 ${!isLast ? 'pb-6' : ''}`}>
        <a href={item.url} target="_blank" rel="noopener noreferrer" className="group inline-flex items-start gap-1">
          <span className="text-sm font-semibold text-gray-900 group-hover:text-blue-600 transition-colors leading-snug">{item.title}</span>
          <ExternalLink className="h-3 w-3 mt-0.5 flex-shrink-0 opacity-0 group-hover:opacity-60 transition-opacity" />
        </a>
        {item.summary && <p className="text-xs text-gray-500 mt-1 leading-relaxed line-clamp-2">{item.summary}</p>}
        <div className="flex items-center gap-2 mt-2">
          <Pill variant="muted">{item.source}</Pill>
          <span className="text-xs text-gray-400">{timeAgo(item.publishedAt)}</span>
        </div>
      </div>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

const RadarNews: React.FC = () => {
  const { data: { news = [] } = {}, isLoading: newsLoading } = useRadarNews(100);
  const [visibleCount, setVisibleCount] = useState(NEWS_PAGE_SIZE);

  const visibleNews = news.slice(0, visibleCount);
  const hasMore = visibleCount < news.length;

  return (
    <>
      <SEO
        title="Security News & Incidents | DomBase Radar"
        description="Latest domain security incidents, breaches, and vulnerability news. Stay informed with DomBase Radar."
        canonical="/radar/news"
      />

      {/* Page header */}
      <div className="border-b border-gray-100 bg-white px-4 sm:px-6 lg:px-8 py-6">
        <div className="max-w-5xl mx-auto">
          <h1 className="text-xl font-bold text-gray-900">Security News &amp; Incidents</h1>
          <p className="text-sm text-gray-400 mt-0.5">Domain security incidents, breaches and vulnerability reports — aggregated in real time</p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Legend */}
        <div className="flex items-center gap-3 mb-6">
          <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Incident Timeline</h2>
          <div className="flex items-center gap-3 text-[10px] text-gray-400">
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500 inline-block" />Critical</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-400 inline-block" />Warning</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-400 inline-block" />Info</span>
          </div>
        </div>

        {newsLoading ? (
          <div className="space-y-6">
            {[1, 2, 3, 4, 5].map(i => (
              <div key={i} className="flex gap-4">
                <div className="w-2.5 h-2.5 rounded-full bg-gray-200 flex-shrink-0 mt-1 animate-pulse" />
                <div className="flex-1 space-y-2 pb-6">
                  <div className="h-4 w-3/4 bg-gray-200 rounded animate-pulse" />
                  <div className="h-3 w-full bg-gray-100 rounded animate-pulse" />
                  <div className="h-3 w-1/2 bg-gray-100 rounded animate-pulse" />
                </div>
              </div>
            ))}
          </div>
        ) : news.length === 0 ? (
          <p className="text-sm text-gray-400">No recent domain security news. Check back soon.</p>
        ) : (
          <>
            <div>
              {visibleNews.map((item, i) => (
                <IncidentRow key={item.id} item={item} isLast={i === visibleNews.length - 1} />
              ))}
            </div>
            {hasMore && (
              <div className="pt-2">
                <Button variant="ghost" size="sm" onClick={() => setVisibleCount(c => c + NEWS_PAGE_SIZE)} className="w-full">
                  <ChevronDown className="h-4 w-4" />
                  Load {Math.min(NEWS_PAGE_SIZE, news.length - visibleCount)} more
                </Button>
              </div>
            )}
            {!hasMore && news.length > NEWS_PAGE_SIZE && (
              <p className="text-xs text-gray-400 text-center pt-4">All {news.length} incidents loaded</p>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default RadarNews;
